import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mail } from 'lucide-react';

/**
 * Soft-retention popup — shown only after a *meaningful* abandonment signal.
 *
 * v1 (deprecated): triggered on mouseleave-top OR rapid scroll-up. The
 * scroll-up heuristic mis-fired whenever users scrolled back through the
 * photo gallery — they got a "Don't leave us!" popup while still actively
 * engaged. We removed that.
 *
 * v2 (current): listens for a single custom event `casaothers:lead-abandoned`
 * which the LeadModal fires when the user closes it WITHOUT submitting.
 * That's a much higher-intent signal — the visitor was interested enough to
 * open the form, then bailed. Offering a low-commitment newsletter sign-up
 * at that exact moment is the textbook retention play.
 *
 * Frequency cap: still once per visitor (localStorage). Once dismissed or
 * submitted, the popup is gone for good for that browser.
 */
const STORAGE_KEY = 'casaothers_retention_popup_seen';

const ExitIntentPopup = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (localStorage.getItem(STORAGE_KEY)) return undefined;

    const onAbandon = () => {
      localStorage.setItem(STORAGE_KEY, '1');
      // Small delay so it doesn't feel jarring while the modal close
      // animation is still finishing.
      setTimeout(() => setOpen(true), 400);
    };

    window.addEventListener('casaothers:lead-abandoned', onAbandon);
    return () => window.removeEventListener('casaothers:lead-abandoned', onAbandon);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim() || 'Anoniem',
          email: email.trim(),
          source: 'retention_popup',
          cta_type: 'newsletter',
          newsletter_optin: true,
          // Message is addressed to Pascal and must stay in Dutch.
          message: 'Newsletter sign-up via retention popup — stuur vergelijkbare woningen.',
        }),
      });
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({ event: 'generate_lead', source: 'retention_popup' });
      }
      setSubmitted(true);
      // Redirect to the central thank-you page so Google Ads can register the
      // conversion against /bedankt (same URL as every other form).
      setOpen(false);
      navigate('/bedankt');
    } catch {
      // Silent — popup will close
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div style={styles.backdrop} onClick={() => setOpen(false)} data-testid="exit-intent-popup">
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => setOpen(false)} style={styles.close} aria-label="Zamknij" data-testid="exit-intent-close">
          <X size={20} />
        </button>

        {submitted ? (
          <div style={styles.success}>
            <h3 style={styles.title}>Dziękujemy!</h3>
            <p style={styles.text}>
              Wkrótce wyślemy Ci starannie dobraną selekcję nieruchomości.
            </p>
          </div>
        ) : (
          <>
            <Mail size={32} color="#C5A059" style={{ marginBottom: '1rem' }} />
            <h3 style={styles.title}>
              Nie znalazłeś jeszcze tego, czego szukasz?
            </h3>
            <p style={styles.text}>
              Zostaw swój e-mail, a wyślemy Ci osobistą selekcję nieruchomości dopasowaną do Twoich potrzeb. Całkowicie bez zobowiązań.
            </p>
            <form onSubmit={submit} style={styles.form}>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Imię (opcjonalnie)"
                style={styles.input}
                data-testid="exit-intent-name"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Twój adres e-mail"
                style={styles.input}
                data-testid="exit-intent-email"
              />
              <button type="submit" disabled={busy} style={styles.submit} data-testid="exit-intent-submit">
                {busy ? '...' : 'Tak, wyślijcie selekcję'}
              </button>
            </form>
            <p style={styles.note}>
              Bez spamu. W każdej chwili możesz się wypisać.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 200,
    backgroundColor: 'rgba(26, 25, 24, 0.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '1.5rem',
    backdropFilter: 'blur(4px)',
    animation: 'fadeIn 0.25s ease',
  },
  modal: {
    position: 'relative', width: '100%', maxWidth: '440px',
    backgroundColor: 'white', padding: '2.5rem 2rem',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
  },
  close: {
    position: 'absolute', top: '0.75rem', right: '0.75rem',
    width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#8C8B88',
  },
  title: {
    fontFamily: "'Playfair Display', serif", fontSize: '1.6rem',
    color: '#1A1918', margin: '0 0 0.65rem', lineHeight: 1.2,
  },
  text: { color: '#585755', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 1.25rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  input: {
    padding: '0.85rem 1rem', fontSize: '0.95rem',
    borderWidth: '1px', borderStyle: 'solid', borderColor: '#EAE5DD',
    backgroundColor: '#FAF9F6', fontFamily: "'Manrope', sans-serif", outline: 'none',
  },
  submit: {
    padding: '0.95rem', backgroundColor: '#C5A059', color: 'white',
    border: 'none', fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', cursor: 'pointer', marginTop: '0.3rem',
    fontFamily: "'Manrope', sans-serif",
  },
  note: { color: '#8C8B88', fontSize: '0.72rem', textAlign: 'center', margin: '0.75rem 0 0' },
  success: { textAlign: 'center', padding: '1rem 0' },
};

export default ExitIntentPopup;
